import { Box, CircularProgress, colors, Stack, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { AWB } from '../../../../_types/awb';
import { useStandardsContext } from '../context';
import LeftDrawer from '_lex/_layouts/TakeoverLayout/LeftDrawer';

interface StandardsListProps {
  selectedStandard?: string;
  setSelectedStandard?: (standardId: string) => void;
  onAddStandard?: (parentId?: string) => void;
  placeholderStandard?: {
    id: string;
    title: string;
    heading: string;
    parentStandardId?: string;
    position: number;
    standardCount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  initialExpandedItems?: string[];
}

const StandardsList: React.FC<StandardsListProps> = ({
  selectedStandard: propSelectedStandard,
  setSelectedStandard: propSetSelectedStandard,
  placeholderStandard,
  initialExpandedItems = [],
}) => {
  const { id: accreditationId, versionId, standardId } = useParams();
  const navigate = useNavigate();
  const {
    version,
    versionUpdateCounter,
    isLoading: standardsLoading,
    navigateToStandard,
    searchStandards,
    expandedItems,
    setExpandedItems,
    searchQuery,
    setSearchQuery,
    toggleExpandedItem,
    getStandardType,
  } = useStandardsContext();

  // Use props if provided, otherwise use URL params and context
  const selectedStandard = propSelectedStandard ?? standardId ?? '';
  const setSelectedStandard =
    propSetSelectedStandard ??
    ((newStandardId: string) => {
      navigate(`/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${newStandardId}`);
    });

  // Find path to selected standard
  const findPathToStandard = useCallback(
    (standards: AWB.Node[], targetId: string, currentPath: string[] = []): string[] | null => {
      for (const standard of standards) {
        if (standard.id === targetId) {
          return [...currentPath, standard.id];
        }
        if ('subStandards' in standard) {
          const path = findPathToStandard(standard.subStandards, targetId, [...currentPath, standard.id]);
          if (path) return path;
        }
      }
      return null;
    },
    []
  );

  // Get path to selected standard or placeholder
  const selectedPath = useMemo(() => {
    const targetId = selectedStandard || placeholderStandard?.id;
    if (!targetId || !version) return [];
    return findPathToStandard(version.standards, targetId) || [];
  }, [selectedStandard, placeholderStandard?.id, version, findPathToStandard, versionUpdateCounter]);

  // Combine user expanded items with selected path
  const combinedExpandedItems = useMemo(() => {
    const combined = new Set([...expandedItems, ...selectedPath]);
    // Add parent of placeholder to expanded items if placeholder exists
    if (placeholderStandard?.parentStandardId) {
      combined.add(placeholderStandard.parentStandardId);
    }
    return Array.from(combined);
  }, [expandedItems, selectedPath, placeholderStandard?.parentStandardId]);

  // Search functionality
  const { items: filteredStandards, paths: searchPaths } = useMemo(() => {
    if (!version) return { items: [], paths: [] };
    return searchStandards(searchQuery);
  }, [version, searchQuery, searchStandards, versionUpdateCounter]);

  // Update expanded items when search paths change
  useEffect(() => {
    if (!isEmpty(searchQuery.trim())) {
      // Reset user expanded items when searching
      setExpandedItems([]);
      const newExpandedItems = new Set<string>();
      searchPaths.forEach((path) => {
        path.forEach((id) => newExpandedItems.add(id));
      });
      setExpandedItems(Array.from(newExpandedItems));
    }
  }, [searchPaths, searchQuery, setExpandedItems]);

  const handleItemClick = async (standardId: string) => {
    try {
      navigateToStandard(standardId);
      setSelectedStandard(standardId);
    } catch (error) {
      // Silent fail for navigation errors
    }
  };

  const renderStandardItem = (node: AWB.Node, level: number = 0) => {
    const itemId = node.id;
    const isPlaceholder = itemId === placeholderStandard?.id;
    const isParentOfPlaceholder = placeholderStandard?.parentStandardId === itemId;
    const nodeType = getStandardType(itemId);
    const hasChildren =
      !isPlaceholder && nodeType !== 'requirement' && (node.standardCount > 0 || isParentOfPlaceholder);

    // Create a placeholder node with required properties
    const placeholderNode = placeholderStandard
      ? (() => {
          const baseNode = {
            ...placeholderStandard,
            draftContent: '',
            publishedContent: '',
          };

          // If parent is a Standard, create a SubStandard
          if ('subStandards' in node) {
            return {
              ...baseNode,
              subStandards: [],
              requirements: [],
            } as AWB.SubStandard;
          }

          // If parent is a SubStandard, create a Requirement
          if ('requirements' in node) {
            return baseNode as AWB.Requirement;
          }

          // Default to Standard
          return {
            ...baseNode,
            subStandards: [],
          } as AWB.Standard;
        })()
      : null;

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <Box
            className='phx-tree-toolbar hover-btn-container'
            onClick={() => !isPlaceholder && handleItemClick(node.id)}
            sx={{
              opacity: isPlaceholder ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                width: '100%',
                alignItems: 'center',
                pr: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden' }}>
                <Typography
                  variant='subtitleSm'
                  sx={{ color: 'slategray', flexShrink: 0 }}
                >
                  {node.heading ? node.heading + ' ' : ''}
                </Typography>
                <Typography
                  variant='subtitleSm'
                  sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {node.title}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            borderBottom: `solid 1px ${colors.grey[300]}`,
            py: 3,
          },
          '& > .MuiTreeItem-content > .MuiTreeItem-iconContainer': {
            visibility: hasChildren ? 'visible' : 'hidden',
          },
        }}
      >
        {'subStandards' in node && (
          <>
            {node.subStandards.map((child) => renderStandardItem(child, level + 1))}
            {isParentOfPlaceholder && placeholderNode && renderStandardItem(placeholderNode, level + 1)}
          </>
        )}
      </TreeItem>
    );
  };

  // Function to count total nodes in the tree
  const countNodes = (standards: AWB.Node[]): number => {
    return standards.reduce((count, standard) => {
      return count + 1 + ('subStandards' in standard ? countNodes(standard.subStandards) : 0);
    }, 0);
  };

  return (
    <LeftDrawer
      open
      header={
        <Stack
          direction='row'
          spacing={4}
          sx={{ flexGrow: 1 }}
        >
          <Typography variant='subtitle1'>{t('awb.standards.list.title')}</Typography>
          {(standardsLoading || !version) && <CircularProgress sx={{ mr: 2 }} />}
        </Stack>
      }
      content={
        <Stack
          spacing={2}
          sx={{ height: '100%' }}
        >
          <Box sx={{ py: 3, px: 4, borderBottom: `solid 1px ${colors.grey[300]}` }}>
            <QuickSearch
              id='keyword_search'
              initialValue={searchQuery}
              onChange={(val) => setSearchQuery(val)}
              placeholder={'Search'}
              fullWidth
            />
          </Box>
          <SimpleTreeView
            selectedItems={selectedStandard}
            expandedItems={combinedExpandedItems}
            onItemExpansionToggle={toggleExpandedItem}
            expansionTrigger='iconContainer'
            sx={{ flex: 1, overflowY: 'auto' }}
          >
            {standardsLoading || !version ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              (searchQuery.trim() ? filteredStandards : version?.standards || []).map((standard) =>
                renderStandardItem(standard)
              )
            )}
          </SimpleTreeView>
        </Stack>
      }
      footer={
        !standardsLoading && (
          <Typography variant='body2'>
            {`${countNodes(searchQuery.trim() ? filteredStandards : version?.standards || [])} standards`}
          </Typography>
        )
      }
      noPadding
    />
  );
};

export default StandardsList;
